import { useEffect, useRef, useState } from 'react';
import {
  StartStreamTranscriptionCommand,
  TranscribeStreamingClient,
} from '@aws-sdk/client-transcribe-streaming';
import type { TranscribeCredentialsResponse } from '@/api/types';
import { prepareSpeechAudioContext } from './speechAudioContext';

const SAMPLE_RATE = 16_000;
const SILENCE_FINALIZE_MS = 1_500;
const AUDIO_LEVEL_REPORT_INTERVAL_MS = 80;
const DEFAULT_RMS_THRESHOLD = 0.004;

type AudioWorkletMessage = {
  pcm: ArrayBuffer;
  rms: number;
};

type TranscriptPart = {
  key: string;
  text: string;
  startTime: number;
};

class AudioChunkQueue {
  private chunks: Uint8Array[] = [];
  private waiters: Array<() => void> = [];
  private closed = false;

  push(chunk: Uint8Array) {
    if (this.closed) return;
    this.chunks.push(chunk);
    this.waiters.splice(0).forEach((resolve) => resolve());
  }

  close() {
    this.closed = true;
    this.waiters.splice(0).forEach((resolve) => resolve());
  }

  async *stream(signal: AbortSignal) {
    while (!this.closed && !signal.aborted) {
      const chunk = this.chunks.shift();
      if (chunk) {
        yield { AudioEvent: { AudioChunk: chunk } };
        continue;
      }
      await new Promise<void>((resolve) => {
        const abort = () => resolve();
        signal.addEventListener('abort', abort, { once: true });
        this.waiters.push(() => {
          signal.removeEventListener('abort', abort);
          resolve();
        });
      });
    }
  }
}

export class TranscriptAccumulator {
  private parts = new Map<string, TranscriptPart>();
  private ignoredKeys = new Set<string>();

  update(part: TranscriptPart) {
    if (!part.text.trim() || this.ignoredKeys.has(part.key)) return;
    this.parts.set(part.key, { ...part, text: part.text.trim() });
  }

  current(): string {
    const ordered = [...this.parts.values()].sort((left, right) => left.startTime - right.startTime);
    return ordered.map(({ text }) => text).join('').trim();
  }

  flush(): string {
    const transcript = this.current();
    const ordered = [...this.parts.values()];
    this.parts.clear();
    ordered.forEach(({ key }) => this.ignoredKeys.add(key));
    if (this.ignoredKeys.size > 100) {
      this.ignoredKeys = new Set([...this.ignoredKeys].slice(-50));
    }
    return transcript;
  }
}

export type UseTranscribeStreamOptions = {
  stream: MediaStream | null;
  active: boolean;
  captureEnabled: boolean;
  getCredentials: () => Promise<TranscribeCredentialsResponse>;
  onReady?: () => void;
  onTranscript: (text: string) => void;
  onPartialTranscript: (text: string) => void;
  onAudioLevel: (rms: number) => void;
  onVoiceActivity: () => void;
  onError: (message: string) => void;
  rmsThreshold?: number;
};

export const createTranscribeAudioChunk = (
  pcm: ArrayBuffer,
  captureEnabled: boolean,
) => {
  const chunk = new Uint8Array(pcm);
  if (!captureEnabled) chunk.fill(0);
  return chunk;
};

export const transcribeErrorText = (error: unknown) => {
  const message = error instanceof Error && error.message ? error.message : String(error);
  if (/no new audio was received for 15 seconds/i.test(message)) {
    return '音声送信が15秒間途切れたため、聞き取りを続けられませんでした';
  }
  return message;
};

export function useTranscribeStream({
  stream,
  active,
  captureEnabled,
  getCredentials,
  onReady,
  onTranscript,
  onPartialTranscript,
  onAudioLevel,
  onVoiceActivity,
  onError,
  rmsThreshold = DEFAULT_RMS_THRESHOLD,
}: UseTranscribeStreamOptions) {
  const captureEnabledRef = useRef(captureEnabled);
  const onReadyRef = useRef(onReady);
  const onTranscriptRef = useRef(onTranscript);
  const onPartialTranscriptRef = useRef(onPartialTranscript);
  const onAudioLevelRef = useRef(onAudioLevel);
  const onVoiceActivityRef = useRef(onVoiceActivity);
  const onErrorRef = useRef(onError);
  captureEnabledRef.current = captureEnabled;
  onReadyRef.current = onReady;
  onTranscriptRef.current = onTranscript;
  onPartialTranscriptRef.current = onPartialTranscript;
  onAudioLevelRef.current = onAudioLevel;
  onVoiceActivityRef.current = onVoiceActivity;
  onErrorRef.current = onError;
  const [credentialRefresh, setCredentialRefresh] = useState(0);

  useEffect(() => {
    if (!active || !stream) return;
    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack) {
      onErrorRef.current('ロボットのマイク音声を受信できません');
      return;
    }

    let mounted = true;
    let audioContext: AudioContext | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    let worklet: AudioWorkletNode | null = null;
    let silentGain: GainNode | null = null;
    let client: TranscribeStreamingClient | null = null;
    let refreshTimer: number | null = null;
    const abortController = new AbortController();
    const queue = new AudioChunkQueue();
    const transcripts = new TranscriptAccumulator();
    let voiceStarted = false;
    let lastVoiceAt = 0;
    let lastActivityReportedAt = 0;
    let lastAudioLevelReportedAt = 0;
    let audioLevelIsZero = true;

    void (async () => {
      try {
        const issued = await getCredentials();
        if (!mounted) return;
        const refreshAfter = Math.max(
          0,
          Date.parse(issued.credentials.expiration) - Date.now() - 120_000,
        );
        refreshTimer = window.setTimeout(() => {
          if (mounted) setCredentialRefresh((current) => current + 1);
        }, refreshAfter);
        audioContext = await prepareSpeechAudioContext();
        if (!mounted) return;

        source = audioContext.createMediaStreamSource(new MediaStream([audioTrack]));
        worklet = new AudioWorkletNode(audioContext, 'pcm16-worklet');
        silentGain = audioContext.createGain();
        silentGain.gain.value = 0;
        source.connect(worklet);
        worklet.connect(silentGain);
        silentGain.connect(audioContext.destination);

        worklet.port.onmessage = (event: MessageEvent<AudioWorkletMessage>) => {
          const { pcm, rms } = event.data;
          const now = Date.now();
          const chunk = createTranscribeAudioChunk(pcm, captureEnabledRef.current);
          queue.push(chunk);
          if (!captureEnabledRef.current) {
            if (!audioLevelIsZero) {
              audioLevelIsZero = true;
              onAudioLevelRef.current(0);
            }
            return;
          }
          if (now - lastAudioLevelReportedAt >= AUDIO_LEVEL_REPORT_INTERVAL_MS) {
            lastAudioLevelReportedAt = now;
            audioLevelIsZero = rms === 0;
            onAudioLevelRef.current(rms);
          }
          if (rms >= rmsThreshold) {
            voiceStarted = true;
            lastVoiceAt = now;
            if (now - lastActivityReportedAt >= 500) {
              lastActivityReportedAt = now;
              onVoiceActivityRef.current();
            }
            return;
          }
          if (!voiceStarted || now - lastVoiceAt < SILENCE_FINALIZE_MS) return;
          const transcript = transcripts.flush();
          voiceStarted = false;
          if (transcript) {
            onPartialTranscriptRef.current('');
            onTranscriptRef.current(transcript);
          }
        };

        client = new TranscribeStreamingClient({
          region: issued.region,
          credentials: {
            accessKeyId: issued.credentials.accessKeyId,
            secretAccessKey: issued.credentials.secretAccessKey,
            sessionToken: issued.credentials.sessionToken,
            expiration: new Date(issued.credentials.expiration),
          },
        });
        const response = await client.send(new StartStreamTranscriptionCommand({
          LanguageCode: 'ja-JP',
          MediaEncoding: 'pcm',
          MediaSampleRateHertz: SAMPLE_RATE,
          AudioStream: queue.stream(abortController.signal),
          EnablePartialResultsStabilization: true,
          PartialResultsStability: 'medium',
        }), { abortSignal: abortController.signal });
        if (!response.TranscriptResultStream) {
          throw new Error('Amazon Transcribe の応答ストリームがありません');
        }
        onReadyRef.current?.();
        for await (const event of response.TranscriptResultStream) {
          if (!mounted || abortController.signal.aborted) break;
          let transcriptUpdated = false;
          for (const result of event.TranscriptEvent?.Transcript?.Results ?? []) {
            const text = result.Alternatives?.[0]?.Transcript?.trim();
            if (!text) continue;
            const key = result.ResultId
              ?? `${result.StartTime ?? 0}:${result.EndTime ?? 0}`;
            transcripts.update({
              key,
              text,
              startTime: result.StartTime ?? 0,
            });
            transcriptUpdated = true;
            if (!voiceStarted) {
              voiceStarted = true;
              lastVoiceAt = Date.now();
              onVoiceActivityRef.current();
            }
          }
          if (transcriptUpdated) {
            onPartialTranscriptRef.current(transcripts.current());
          }
        }
        if (mounted && !abortController.signal.aborted) {
          throw new Error('Amazon Transcribe との接続が終了しました');
        }
      } catch (error) {
        if (!mounted || abortController.signal.aborted) return;
        onErrorRef.current(transcribeErrorText(error));
      }
    })();

    return () => {
      onAudioLevelRef.current(0);
      onPartialTranscriptRef.current('');
      mounted = false;
      abortController.abort();
      queue.close();
      if (worklet) {
        worklet.port.onmessage = null;
        worklet.disconnect();
      }
      source?.disconnect();
      silentGain?.disconnect();
      if (refreshTimer !== null) window.clearTimeout(refreshTimer);
      client?.destroy();
    };
  }, [active, credentialRefresh, getCredentials, rmsThreshold, stream]);
}
